/**
 * Discriminated-union Zod schema for the 5 appraisal question-answer shapes.
 *
 * **Why this lives here, not in `@linkhr/api-client`:** the backend exposes
 * `AnswerInput` as a flat optional bag (`{ questionId, ratingValue?, textValue?,
 * selectedOptionIds? }`) — it has no per-type discrimination. The FE needs a
 * narrower contract that pins the right field per `questionType` so:
 *   1. Wave 1 template-editor RHF (Plan 03) can validate per-type shape.
 *   2. Wave 2 reviewer flow (Plan 07/08) can drive the required-question gate
 *      via `isAnswerComplete` before sending the submit POST.
 *   3. Wave 3 export verification (Plan 11b) parses CSV/XLSX rows safely.
 *
 * The 5 backend `QuestionType` literals come from
 * `components["schemas"]["QuestionView"].questionType` in the generated
 * client. Order matches the backend enum declaration to keep grep + diff
 * tooling happy.
 *
 * **Draft-friendly:** every per-variant field is optional so an in-progress
 * autosave doesn't fail validation. The required-question gate is a separate
 * concern handled by `isAnswerComplete` (called pre-submit, not pre-save).
 */
import { z } from "zod";

export const QUESTION_TYPES = [
  "RATING_5",
  "RATING_10",
  "TEXT",
  "CHOICE_SINGLE",
  "CHOICE_MULTI",
] as const;

export type QuestionType = (typeof QUESTION_TYPES)[number];

/**
 * Discriminated union over `questionType`.
 *
 * Per-variant invariants (Zod-enforced):
 *   - RATING_5:      ratingValue ∈ [1, 5] (optional for drafts)
 *   - RATING_10:     ratingValue ∈ [1, 10] (optional for drafts)
 *   - TEXT:          textValue is an arbitrary string (optional for drafts;
 *                    length constraints belong on the renderer / submit gate)
 *   - CHOICE_SINGLE: selectedOptionIds has 0 or 1 uuid (drafts allow zero)
 *   - CHOICE_MULTI:  selectedOptionIds is an arbitrary uuid[] (drafts allow zero)
 */
export const questionAnswerSchema = z.discriminatedUnion("questionType", [
  z.object({
    questionType: z.literal("RATING_5"),
    ratingValue: z.number().int().min(1).max(5).optional(),
  }),
  z.object({
    questionType: z.literal("RATING_10"),
    ratingValue: z.number().int().min(1).max(10).optional(),
  }),
  z.object({
    questionType: z.literal("TEXT"),
    textValue: z.string().optional(),
  }),
  z.object({
    questionType: z.literal("CHOICE_SINGLE"),
    selectedOptionIds: z.array(z.string().uuid()).max(1).optional(),
  }),
  z.object({
    questionType: z.literal("CHOICE_MULTI"),
    selectedOptionIds: z.array(z.string().uuid()).optional(),
  }),
]);

export type QuestionAnswer = z.infer<typeof questionAnswerSchema>;

/**
 * Required-question gate for Plan 07/08's submit-time refinement.
 *
 * Returns `true` when one of:
 *   - the question is not required (always considered complete), OR
 *   - the answer satisfies the per-type "non-empty" rule:
 *       - RATING_*: ratingValue is a number (not undefined)
 *       - TEXT:     textValue trims to a non-empty string
 *       - CHOICE_*: selectedOptionIds has length ≥ 1
 *
 * Returns `false` when required AND missing/empty per the rules above.
 *
 * Mirrors the backend `APPRAISAL_REVIEW_REQUIRED_UNANSWERED` 422 server-side
 * check so the FE can scroll-to-first-error before the round-trip cost.
 */
export function isAnswerComplete(answer: QuestionAnswer | undefined, required: boolean): boolean {
  if (!required) return true;
  if (!answer) return false;

  switch (answer.questionType) {
    case "RATING_5":
    case "RATING_10":
      return typeof answer.ratingValue === "number";
    case "TEXT":
      return !!answer.textValue && answer.textValue.trim().length > 0;
    case "CHOICE_SINGLE":
    case "CHOICE_MULTI":
      return (answer.selectedOptionIds?.length ?? 0) > 0;
  }
}
